'use client';

/**
 * Calendar — react-day-picker v9 기반 본 구현 (PR-Z1, ds-react 0.4.29).
 *
 * - mode="single" 만 지원. range/multiple 은 후속 PR (RangeCalendar / DateRangePicker).
 * - locale default = `ko` (date-fns/locale). caller 가 enUS 등 명시 override 가능.
 * - labels default = 한국어 ARIA 라벨 (KO_LABELS). caller override 시 spread merge.
 * - chevron = `@crabs-ai/web-icons` ChevronLeft/Right (lucide 미사용 — DESIGN.md §7).
 * - 토큰 = sys-* / comp-* (DESIGN.md §4.0 / §4.6).
 * - DESIGN.md §7 nested-interactive escape hatch: ARIA 1.2 grid pattern gridcell+button 정식.
 *
 * caller 패턴 (RHF Controller form 통합):
 * ```tsx
 * <Controller name="targetDate" render={({ field }) => (
 *   <Calendar
 *     mode="single"
 *     selected={parseYyyymmdd(field.value)}
 *     onSelect={(d) => d && field.onChange(formatYyyymmdd(d))}
 *   />
 * )} />
 * ```
 */

import { ko } from 'date-fns/locale';
import { format } from 'date-fns';
import * as React from 'react';
import {
  DayFlag,
  DayPicker,
  SelectionState,
  UI,
  type ChevronProps,
  type Labels,
} from 'react-day-picker';
import { ChevronLeft, ChevronRight } from '@crabs-ai/web-icons';

import { cn } from '../../lib/utils';

const KO_LABELS: Partial<Labels> = {
  labelDayButton: (date, modifiers) => {
    let label = format(date, 'PPPP', { locale: ko });
    if (modifiers.today) label = `오늘, ${label}`;
    if (modifiers.selected) label = `${label}, 선택됨`;
    if (modifiers.disabled) label = `${label}, 비활성`;
    return label;
  },
  labelNext: () => '다음 달로 이동',
  labelPrevious: () => '이전 달로 이동',
  labelNav: () => '월 이동',
  labelGrid: (date) => format(date, 'yyyy년 M월', { locale: ko }),
  labelMonthDropdown: () => '월 선택',
  labelYearDropdown: () => '연도 선택',
  labelWeekday: (date) => format(date, 'EEEE', { locale: ko }),
};

function Chevron({ orientation, className, ...rest }: ChevronProps) {
  if (orientation === 'left')
    return (
      <ChevronLeft
        className={cn('size-4', className)}
        {...rest}
      />
    );
  if (orientation === 'right')
    return (
      <ChevronRight
        className={cn('size-4', className)}
        {...rest}
      />
    );
  return (
    <ChevronRight
      className={cn('size-4', className)}
      {...rest}
    />
  );
}

const CALENDAR_CLASSNAMES = {
  [UI.Root]:
    'rounded-md bg-comp-calendar-bg p-md max-w-full overflow-x-auto border border-comp-calendar-border',
  [UI.Months]: 'relative flex flex-col gap-md',
  [UI.Month]: 'flex flex-col gap-sm',
  [UI.MonthCaption]: 'relative flex h-7 items-center justify-center',
  [UI.CaptionLabel]: 'text-title-large text-comp-calendar-caption-text',
  [UI.Nav]: 'absolute inset-x-0 top-0 z-10 flex items-center justify-between',
  [UI.PreviousMonthButton]:
    'inline-flex size-7 items-center justify-center rounded-2xs bg-transparent text-comp-calendar-nav-text hover:bg-comp-calendar-nav-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-comp-calendar-focus-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-30',
  [UI.NextMonthButton]:
    'inline-flex size-7 items-center justify-center rounded-2xs bg-transparent text-comp-calendar-nav-text hover:bg-comp-calendar-nav-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-comp-calendar-focus-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-30',
  [UI.MonthGrid]: 'w-full border-collapse',
  [UI.Weekdays]: 'flex',
  [UI.Weekday]:
    'w-9 text-center font-medium text-caption-medium text-comp-calendar-weekday-text',
  [UI.Week]: 'mt-2xs flex w-full',
  [UI.Day]:
    'relative p-0 text-center text-body-medium focus-within:relative focus-within:z-20',
  [UI.DayButton]:
    'inline-flex size-9 items-center justify-center rounded-2xs p-0 font-regular text-comp-calendar-day-text hover:bg-comp-calendar-day-bg-hover active:bg-comp-calendar-day-bg-active focus:outline-none focus-visible:ring-2 focus-visible:ring-comp-calendar-focus-ring focus-visible:ring-offset-2',
  [UI.Footer]:
    'pt-sm text-center text-caption-medium text-comp-calendar-weekday-text',
  [DayFlag.today]: 'border border-comp-calendar-today-border font-bold',
  [DayFlag.outside]:
    'text-comp-calendar-outside-text [&_button]:text-comp-calendar-outside-text [&_button]:opacity-60',
  [DayFlag.disabled]:
    'cursor-not-allowed text-comp-calendar-disabled-text opacity-50 [&_button]:cursor-not-allowed',
  [DayFlag.hidden]: 'invisible',
  [SelectionState.selected]:
    '[&_button]:bg-comp-calendar-selected-bg [&_button]:font-medium [&_button]:text-comp-calendar-selected-text [&_button:hover]:bg-comp-calendar-selected-bg [&_button:hover]:text-comp-calendar-selected-text [&_button:focus]:bg-comp-calendar-selected-bg [&_button:focus]:text-comp-calendar-selected-text',
};

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  showOutsideDays = true,
  locale = ko,
  labels,
  classNames,
  components,
  month: monthProp,
  defaultMonth,
  onMonthChange,
  footer,
  ...props
}: CalendarProps) {
  const [internalMonth, setInternalMonth] = React.useState<Date | undefined>(
    defaultMonth,
  );
  const isControlled = monthProp !== undefined;
  const currentMonth = isControlled ? monthProp : internalMonth;

  const handleMonthChange = (m: Date) => {
    if (!isControlled) setInternalMonth(m);
    onMonthChange?.(m);
  };

  const defaultFooter =
    footer === null ? null : (
      <button
        type="button"
        onClick={() => handleMonthChange(new Date())}
        className="text-caption-medium text-comp-calendar-link-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-comp-calendar-focus-ring focus-visible:ring-offset-2 rounded-2xs px-2xs"
      >
        오늘로 이동
      </button>
    );

  return (
    <DayPicker
      data-slot="calendar"
      data-mode={props.mode ?? 'single'}
      showOutsideDays={showOutsideDays}
      locale={locale}
      labels={{ ...KO_LABELS, ...labels }}
      classNames={{ ...CALENDAR_CLASSNAMES, ...classNames }}
      components={{ Chevron, ...components }}
      month={currentMonth}
      onMonthChange={handleMonthChange}
      footer={footer ?? defaultFooter}
      className={cn(className)}
      {...props}
    />
  );
}

export { Calendar };
